import { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import '../i18n/config';

const LanguageContext = createContext();

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

export const LanguageProvider = ({ children }) => {
    const { t, i18n } = useTranslation();
    const [language, setLanguage] = useState(i18n.language || 'he');
    const [direction, setDirection] = useState(language === 'he' ? 'rtl' : 'ltr');

    useEffect(() => {
        // Sync state if i18n language changes externally
        if (i18n.language && i18n.language !== language) {
            setLanguage(i18n.language);
            setDirection(i18n.language === 'he' ? 'rtl' : 'ltr');
        }
    }, [i18n.language]);

    useEffect(() => {
        // Update HTML direction and lang attributes
        document.documentElement.setAttribute('dir', direction);
        document.documentElement.setAttribute('lang', language);
    }, [direction, language]);

    const switchLanguage = (lang) => {
        i18n.changeLanguage(lang);
        setLanguage(lang);
        setDirection(lang === 'he' ? 'rtl' : 'ltr');
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat(language === 'he' ? 'he-IL' : 'en-US', {
            style: 'currency',
            currency: 'ILS',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    return (
        <LanguageContext.Provider value={{ language, direction, switchLanguage, t, formatCurrency }}>
            {children}
        </LanguageContext.Provider>
    );
};
